/**
 * Fehlergrund → Darstellung (Vertrag §6).
 * `grundVon()` aus api.ts liefert den Code.
 */

export type MeldungsArt =
  | "nicht_vorhanden"
  | "nur_lesend"
  | "rolle_fehlt"
  | "versionskonflikt"
  | "anmeldung"
  | "netz"
  | "sonstiges";

export type Meldung = {
  art: MeldungsArt;
  text: string;
  /** Bei Versionskonflikt: Eingabe behalten, Neu laden anbieten. */
  eingabeBehalten: boolean;
  neuLadenAnbieten: boolean;
  erneutVersuchen: boolean;
};

/** Alias laut Vertrag: `meldungZu(grund)`. */
export function meldungZu(
  grund: string | null,
  optionen: { netzfehler?: boolean; fallback?: string } = {},
): Meldung {
  if (optionen.netzfehler) {
    return {
      art: "netz",
      text: "Server nicht erreichbar. Die Liste bleibt sichtbar — bitte erneut versuchen.",
      eingabeBehalten: true,
      neuLadenAnbieten: false,
      erneutVersuchen: true,
    };
  }

  switch (grund) {
    case "nicht_sichtbar":
    case "nicht_vorhanden":
      return {
        art: "nicht_vorhanden",
        text: "Diesen Eintrag gibt es nicht.",
        eingabeBehalten: false,
        neuLadenAnbieten: false,
        erneutVersuchen: false,
      };
    case "nur_lesend":
      return {
        art: "nur_lesend",
        text: "Nur Lesen — Schreiben ist hier nicht erlaubt.",
        eingabeBehalten: true,
        neuLadenAnbieten: false,
        erneutVersuchen: false,
      };
    case "rolle_zu_niedrig":
      return {
        art: "rolle_fehlt",
        text: "Dafür fehlt die passende Projektrolle.",
        eingabeBehalten: true,
        neuLadenAnbieten: false,
        erneutVersuchen: false,
      };
    case "versionskonflikt":
      return {
        art: "versionskonflikt",
        text: "Jemand anderes war schneller. Neu laden und erneut speichern — deine Eingabe bleibt erhalten.",
        eingabeBehalten: true,
        neuLadenAnbieten: true,
        erneutVersuchen: false,
      };
    case "anmeldung":
    case "401":
      return {
        art: "anmeldung",
        text: "Sitzung abgelaufen. Bitte erneut anmelden.",
        eingabeBehalten: true,
        neuLadenAnbieten: false,
        erneutVersuchen: false,
      };
    case "gitlab_abweichung":
      return {
        art: "sonstiges",
        text:
          optionen.fallback ??
          "GitLab hat den Merge nicht bestätigt. Lies den Merge Request neu.",
        eingabeBehalten: true,
        neuLadenAnbieten: false,
        erneutVersuchen: true,
      };
    case "qualitaets_gate":
      return {
        art: "sonstiges",
        text:
          optionen.fallback ??
          "Das Qualitäts-Gate hält diesen Merge auf.",
        eingabeBehalten: true,
        neuLadenAnbieten: false,
        erneutVersuchen: false,
      };
    case "gitlab_nicht_eingerichtet":
      return {
        art: "sonstiges",
        text:
          optionen.fallback ??
          "Der GitLab-Schreibzugang ist nicht eingerichtet.",
        eingabeBehalten: true,
        neuLadenAnbieten: false,
        erneutVersuchen: false,
      };
    default:
      return {
        art: "sonstiges",
        text: optionen.fallback ?? "Etwas ist schiefgelaufen.",
        eingabeBehalten: true,
        neuLadenAnbieten: false,
        erneutVersuchen: false,
      };
  }
}

/**
 * Der Serverhinweis schlägt den Standardtext — außer bei Netz und abgelaufener
 * Sitzung, die ihre eigene, sichere Formulierung behalten.
 */
export function meldungMitHinweis(
  grund: string | null,
  hinweis: string | null | undefined,
  optionen: { netzfehler?: boolean; fallback?: string } = {},
): Meldung {
  const basis = meldungZu(grund, {
    ...optionen,
    fallback: hinweis?.trim() || optionen.fallback,
  });
  if (optionen.netzfehler || grund === "401" || grund === "anmeldung") {
    return basis;
  }
  const text = hinweis?.trim();
  if (text) return { ...basis, text };
  return basis;
}

/** @deprecated Name aus früherem Entwurf — nutze `meldungZu`. */
export const meldungAusGrund = meldungZu;
